import { FC, useEffect, useState } from 'react';
import { Box, Button, DialogActions, FormControl, InputLabel, MenuItem, Paper, Select, TableContainer } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { DataGrid, GridCellEditCommitParams, GridColumns } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import { LoadingSpinner } from '@components';
import { generateUniqueKey, toCurrency } from '@helpers';
import { BulkPricingRule, Product } from '@models';
import { useCreateBulkPricingRuleMutation, useGetBulkPricingRulesQuery, useUpdateBulkPricingRuleMutation } from '@services';

/**
 * An extended interface is needed because new rules won't have ID's until BigCommerce generates them.
 * This is a problem because the DataGrid component needs all rows to have ID's.
 * The tempId property is only used for the DataGrid to work; the actual ID will ultimately come from BigCommerce.
 */
interface EditableBulkPricingRule extends BulkPricingRule {
  tempId?: string;
}

type Props = {
  product: Product;
  onClose: () => void;
};

export const BulkPricingTab: FC<Props> = ({ product, onClose }) => {
  const { data: bulkPricingRules, isFetching, refetch } = useGetBulkPricingRulesQuery(product.id);
  const [editableRules, setEditableRules] = useState<EditableBulkPricingRule[]>([]);
  const [type, setType] = useState<BulkPricingRule['type']>('fixed');

  // Set the editable rules and type whenever new rules are fetched from BigCommerce.
  useEffect(() => {
    if (!bulkPricingRules?.length) return;

    setEditableRules([...bulkPricingRules]);
    setType(bulkPricingRules[0]?.type);
  }, [bulkPricingRules]);

  const getType = () => {
    if (type === 'fixed') return 'Fixed Amount';

    if (type === 'percent') return '% Discount';

    if (type === 'price') return '$ Off/Unit';
  };

  const formatRuleAmount = (amount: number) => {
    if (type === 'fixed' || type === 'price') {
      return toCurrency(amount);
    }

    if (type === 'percent') {
      return `${amount}%`;
    }
  };

  const getUnitPrice = (ruleAmount: number) => {
    if (type === 'fixed') {
      return toCurrency(ruleAmount);
    }

    if (product?.price) {
      if (type === 'percent') {
        return toCurrency(product.price * (1 - ruleAmount / 100));
      }

      if (type === 'price') {
        return toCurrency(product?.price - ruleAmount);
      }
    }
  };

  const columns: GridColumns = [
    {
      field: 'quantity_min',
      headerName: 'Minimum Quantity',
      type: 'number',
      flex: 1,
      editable: true,
      preProcessEditCellProps: (params) => {
        const value = params.props.value as number;

        if (value < 0) {
          return { ...params.props, error: true };
        }

        let hasError = false;

        const currentRule = editableRules.find((rule) => {
          if (rule.id) {
            return rule.id === params.id;
          }

          if (rule.tempId) {
            return rule.tempId === params.id;
          }

          return false;
        });

        // Editing an existing rule.
        if (currentRule) {
          // When quantity_max is 0, it means that there is no maximum quantity.
          if (currentRule.quantity_max !== 0 && value >= currentRule.quantity_max) {
            hasError = true;
          }
        }

        return { ...params.props, error: hasError };
      },
    },
    {
      field: 'quantity_max',
      headerName: 'Maximum Quantity',
      type: 'number',
      flex: 1,
      editable: true,
      preProcessEditCellProps: (params) => {
        const value = Number(params.props.value);

        if (value < 0) {
          return { ...params.props, error: true };
        }

        let hasError = false;

        const currentRule = editableRules.find((rule) => {
          if (rule.id) {
            return rule.id === params.id;
          }

          if (rule.tempId) {
            return rule.tempId === params.id;
          }

          return false;
        });

        if (currentRule) {
          if (value < currentRule.quantity_min) {
            if (value === 0) {
              const ruleWithHigherMinimumQuantity = !!editableRules.find((rule) => rule.quantity_min > currentRule.quantity_min);

              // Value can be 0 if the current rule has the highest minimum quantity.
              hasError = ruleWithHigherMinimumQuantity ? true : false;
            } else {
              hasError = true;
            }
          }

          editableRules.forEach((rule) => {
            if (rule.id === currentRule.id || rule.tempId === currentRule.tempId) return;

            if (currentRule.quantity_min < rule.quantity_min && currentRule.quantity_max >= rule.quantity_min) {
              return (hasError = true);
            }

            // It shouldn't be possible to set a maximum value that is
            // greater than the minimum value of another existing rule,
            // if the other rule's minimum quantity is lower than the current rule's.
            if (currentRule.quantity_min < rule.quantity_min && value >= rule.quantity_min) {
              hasError = true;
            }
          });
        }

        return { ...params.props, error: hasError };
      },
    },
    {
      field: 'amount',
      headerName: getType(),
      type: 'number',
      flex: 1,
      editable: true,
      valueFormatter: ({ value }) => formatRuleAmount(Number(value)),
      preProcessEditCellProps: (params) => {
        const value = Number(params.props.value);

        let hasError = false;

        if (isNaN(value)) {
          hasError = true;
        }

        return { ...params.props, error: hasError };
      },
    },
    {
      field: 'unit_price',
      headerName: 'Unit Price',
      type: 'number',
      flex: 1,
    },
  ];

  const rows = editableRules.map((rule) => {
    return {
      id: rule.id || rule.tempId,
      quantity_min: rule.quantity_min,
      quantity_max: rule.quantity_max,
      amount: rule.amount,
      unit_price: getUnitPrice(rule.amount),
    };
  });

  /**
   * Updates `editableRules` whenever the value of any cell changes to avoid being out of sync.
   */
  const handleCellChange = (params: GridCellEditCommitParams) => {
    const rules = [...editableRules];

    const existingIndex = editableRules.findIndex((rule) => {
      if (rule.id) {
        return rule.id === params.id;
      }

      if (rule.tempId) {
        return rule.tempId === params.id;
      }

      return false;
    });

    if (existingIndex >= 0) {
      rules[existingIndex] = {
        ...rules[existingIndex],
        [params.field]: params.value,
      };
    }

    setEditableRules(rules);
  };

  const createNewRule = () => {
    /**
     * Gets the largest existing maximum quantity and returns it + 1.
     */
    const getInitialMinimumQuantity = (): number => {
      const index = editableRules.findIndex((rule) => rule.quantity_max === 0);

      if (index >= 0) {
        const rules = [...editableRules];

        rules[index].quantity_max = editableRules[index].quantity_min;

        setEditableRules([...rules]);
      }

      return editableRules.reduce((newMinimum, rule) => (rule.quantity_max + 1 > newMinimum ? rule.quantity_max + 1 : newMinimum), 1);
    };

    setEditableRules([
      ...editableRules,
      {
        amount: 0,
        quantity_max: 0,
        quantity_min: getInitialMinimumQuantity(),
        tempId: generateUniqueKey(),
        type,
      } as EditableBulkPricingRule,
    ]);
  };

  const [createBulkPricingRule, { isLoading: createIsLoading }] = useCreateBulkPricingRuleMutation();
  const [updateBulkPricingRule, { isLoading: updateIsLoading }] = useUpdateBulkPricingRuleMutation();

  const handleSave = async () => {
    // Array that will contain all new/updated rules.
    const changedRules: EditableBulkPricingRule[] = [];

    // If there were no previous rules and new ones have been added.
    if (!bulkPricingRules?.length && editableRules.length) {
      changedRules.push(...editableRules);
    } else if (bulkPricingRules?.length) {
      // If type has changed, add all rules to be updated.
      if (bulkPricingRules?.length && bulkPricingRules[0].type !== type) {
        changedRules.push(
          ...editableRules.map((rule) => ({
            ...rule,
            type,
          })),
        );
      } else {
        editableRules.forEach((rule, index) => {
          // No need to evaluate each field when dealing with a new rule.
          if (rule.tempId) {
            return changedRules.push(rule);
          }

          // For existing rules, check whether any individual attributes have changed.
          for (const [key, value] of Object.entries(rule)) {
            // Type is set to `any` because it would otherwise complain about the key being a string
            // whereas that isn't the correct type on the BulkPricingRule interface. This is done to
            // allow for easily iterating over the keys without spelling them all out individually.
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            if (value !== (bulkPricingRules[index] as any)[key]) {
              changedRules.push(rule);
              break;
            }
          }
        });
      }
    }

    if (changedRules.length) {
      const newRules: Partial<BulkPricingRule>[] = [];
      const updatedRules: BulkPricingRule[] = [];

      changedRules.forEach((rule) => (rule.tempId ? newRules.push(rule) : updatedRules.push(rule)));

      if (newRules.length) {
        await Promise.allSettled(newRules.map(async (rule) => createBulkPricingRule({ productId: product.id, rule })));
      }

      if (updatedRules.length) {
        await Promise.allSettled(updatedRules.map(async (rule) => updateBulkPricingRule({ productId: product.id, rule })));
      }

      // Get the new/updated rules from BigCommerce to ensure synchronization.
      refetch();
    }
  };

  return (
    <>
      {isFetching && <LoadingSpinner isOpen />}
      <Box>
        {bulkPricingRules && (
          <Box display="flex" justifyContent="space-between" marginBottom={2}>
            <FormControl>
              <InputLabel id="type-label">Type</InputLabel>
              <Select
                value={type}
                size="small"
                id="type-select"
                label="Type"
                labelId="type-label"
                onChange={(event) => setType(event.target.value as 'fixed' | 'price' | 'percent')}
              >
                <MenuItem value="fixed">Fixed Amount</MenuItem>
                <MenuItem value="percent">Percentage Discount</MenuItem>
                <MenuItem value="price">$ Off/Unit</MenuItem>
              </Select>
            </FormControl>
            <Button variant="contained" data-testid="create-new-rule-button" onClick={createNewRule}>
              <AddIcon />
            </Button>
          </Box>
        )}
        <TableContainer component={Paper} sx={{ height: '368px', width: '100%' }}>
          {rows && <DataGrid columns={columns} rows={rows} onCellEditCommit={handleCellChange} />}
        </TableContainer>
      </Box>
      <DialogActions sx={{ justifyContent: 'flex-end', mb: -4, mt: 2, pr: 0 }}>
        <Button data-testid="bulk-price-rules-cancel" type="button" variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <LoadingButton
          data-testid="save-bulk-price-rules"
          loading={createIsLoading || updateIsLoading}
          variant="contained"
          onClick={handleSave}
        >
          Save Rules
        </LoadingButton>
      </DialogActions>
    </>
  );
};
